import type { Psr } from '@skewb-works/contracts';
import { KeyRound, ListChecks, ShieldAlert, User } from 'lucide-react';
import { InfoRow } from './InfoRow';

interface PSRDetailsCardProps {
  psr: Psr | null | undefined;
}

/**
 * "PSR Details" card for the job detail page. Shows the Priority Services
 * Register information for a vulnerable customer (WRK-5510).
 *
 * - No PSR data at all (or only falsy flags and no fields) → renders nothing.
 * - Flagged (fieldIdentified/iid) but no detail fields → renders the card with
 *   a fallback message so the page still signals it's a PSR job.
 * - Otherwise → renders only the non-null detail fields.
 */
export function PSRDetailsCard({ psr }: PSRDetailsCardProps) {
  if (!psr) return null;

  const rows = [
    { key: 'priority', label: 'Priority', value: psr.priority, icon: ShieldAlert },
    { key: 'needs', label: 'Needs', value: psr.needs, icon: ListChecks },
    { key: 'contactName', label: 'Contact Name', value: psr.contactName, icon: User },
    { key: 'password', label: 'Password', value: psr.password, icon: KeyRound },
    { key: 'xip', label: 'XIP', value: psr.xip, icon: ListChecks },
  ].filter((row): row is typeof row & { value: string } => Boolean(row.value));

  const isFlagged = psr.fieldIdentified === true || psr.iid === true;

  // Nothing meaningful to show: not flagged and no populated fields.
  if (rows.length === 0 && !isFlagged) return null;

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-amber-50 dark:bg-amber-950 flex items-center justify-center">
          <ShieldAlert className="w-5 h-5 text-amber-600 dark:text-amber-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">PSR Details</h3>
          <p className="text-xs text-muted-foreground">Priority Services Register</p>
        </div>
      </div>

      {rows.length > 0 ? (
        <div className="space-y-4">
          {rows.map((row) => (
            <InfoRow key={row.key} icon={row.icon} label={row.label} value={row.value} />
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          No additional PSR details were provided for this job.
        </p>
      )}
    </div>
  );
}
